/**
 * Statistiques en direct (admin) — RPC `admin_live_stats` (migration 0697) : chiffres clés calculés à la
 * demande, comparés à la période d'avant de même durée, avec une courbe par chiffre. Mêmes règles que le récap
 * journalier (robots et comptes internes exclus). Les règles d'affichage sont pures et testées.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export type StatsRange = 'day' | 'week' | 'month';
export type StatsWindow = 'hour' | 'today' | 'week' | 'month';

export interface StatsMetrics {
  visitors: number;
  views: number;
  sessions: number;
  demos: number;
  signups: number;
  active: number;
  sales: number;
  revenue_cents: number;
  bot_views: number;
}

export type MetricKey = 'visitors' | 'views' | 'signups' | 'active' | 'demos' | 'revenue_cents';

export interface StatsPair {
  cur: StatsMetrics;
  prev: StatsMetrics;
}

export interface StatsPoint extends StatsPair {
  t: string;
}

export interface TopPage {
  path: string;
  /** Titre du contenu (carte, pack, exercice…), migration 0700 ; null sinon. */
  label?: string | null;
  views: number;
  visitors: number;
  prev_views: number;
}

export interface LiveStats {
  generated_at: string;
  range: StatsRange;
  bucket: 'hour' | 'day';
  now: {
    visitors_5min: number;
    pages_5min: {
      app: 'site' | 'app';
      path: string;
      label?: string | null;
      visitors: number;
    }[];
  };
  windows: Record<StatsWindow, StatsPair>;
  range_totals: StatsPair;
  series: StatsPoint[];
  top_site: TopPage[];
  top_app: TopPage[];
  platforms: Partial<Record<'site' | 'app_web' | 'ios' | 'android', number>>;
  bots: Record<string, number>;
}

export const STATS_RANGES: { id: StatsRange; label: string; prevLabel: string }[] = [
  { id: 'day', label: '24 h', prevLabel: 'les 24 h d’avant' },
  { id: 'week', label: '7 jours', prevLabel: 'les 7 jours d’avant' },
  { id: 'month', label: '30 jours', prevLabel: 'les 30 jours d’avant' },
];

export const STATS_WINDOWS: { id: StatsWindow; label: string; vs: string }[] = [
  { id: 'hour', label: 'Dernière heure', vs: 'vs l’heure d’avant' },
  { id: 'today', label: 'Aujourd’hui', vs: 'vs hier à la même heure' },
  { id: 'week', label: '7 derniers jours', vs: 'vs les 7 jours d’avant' },
  { id: 'month', label: '30 derniers jours', vs: 'vs les 30 jours d’avant' },
];

export const STATS_METRICS: { key: MetricKey; label: string; help: string }[] = [
  {
    key: 'visitors',
    label: 'Visiteurs uniques',
    help: 'Un compte connecté compte une fois ; un visiteur sans compte, une fois par jour.',
  },
  { key: 'views', label: 'Pages vues', help: 'Site et app, robots exclus.' },
  { key: 'signups', label: 'Inscriptions', help: 'Nouveaux comptes (hors comptes internes).' },
  {
    key: 'active',
    label: 'Comptes actifs',
    help: 'Comptes qui ont ouvert une page ou commencé un exercice.',
  },
  { key: 'demos', label: 'Démos lancées', help: 'Démos commencées sans compte.' },
  { key: 'revenue_cents', label: 'Ventes SKILLCARDS', help: 'Achats payés, hors remboursés.' },
];

/** Libellé lisible d'une famille de robots. */
export const BOT_LABELS: Record<string, string> = {
  google: 'Google',
  bing: 'Bing',
  apple: 'Apple',
  yandex: 'Yandex',
  baidu: 'Baidu',
  duckduckgo: 'DuckDuckGo',
  meta: 'Meta',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  perplexity: 'Perplexity',
  bytedance: 'ByteDance',
  seo: 'Outils SEO',
  monitor: 'Surveillance',
  headless: 'Navigateurs automatisés',
  webdriver: 'Navigateurs pilotés',
  burst: 'Rafales',
  no_ua: 'Sans navigateur',
  other: 'Autres robots',
};

const INT = new Intl.NumberFormat('fr-FR');
const EURO = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

/** Valeur affichée d'un chiffre (les ventes en euros). */
export function formatMetric(key: MetricKey, v: number): string {
  return key === 'revenue_cents' ? EURO.format(v / 100) : INT.format(v);
}

export interface Trend {
  dir: 'up' | 'down' | 'flat';
  /** « +12 % », « −3 % », « nouveau », « = » */
  label: string;
}

/** Évolution par rapport à la période d'avant. */
export function trend(cur: number, prev: number): Trend {
  if (cur === prev) return { dir: 'flat', label: '=' };
  if (prev === 0) return { dir: 'up', label: 'nouveau' };
  const pct = Math.round(((cur - prev) / prev) * 100);
  const dir = cur > prev ? 'up' : 'down';
  if (pct === 0) return { dir, label: dir === 'up' ? '+ <1 %' : '− <1 %' };
  return { dir, label: `${pct > 0 ? '+' : '−'}${Math.abs(pct)} %` };
}

const HOUR = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit' });
const DAY = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  weekday: 'short',
  day: 'numeric',
});
const DAY_LONG = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/** Libellé court d'une tranche : « 14 h » ou « mar. 28 ». */
export function bucketLabel(t: string, bucket: 'hour' | 'day'): string {
  const d = new Date(t);
  return bucket === 'hour' ? `${HOUR.format(d).replace(/\D/g, '')} h` : DAY.format(d);
}

/** Libellé complet d'une tranche (infobulle) : « 14 h – 15 h » ou « mardi 28 octobre ». */
export function bucketTitle(t: string, bucket: 'hour' | 'day'): string {
  const d = new Date(t);
  if (bucket === 'day') return DAY_LONG.format(d);
  const h = Number(HOUR.format(d).replace(/\D/g, ''));
  return `${h} h – ${(h + 1) % 24} h`;
}

/** Les deux suites de valeurs d'un chiffre (période choisie, période d'avant). */
export function metricSeries(
  series: StatsPoint[],
  key: MetricKey,
): { cur: number[]; prev: number[] } {
  return {
    cur: series.map((p) => p.cur[key] ?? 0),
    prev: series.map((p) => p.prev[key] ?? 0),
  };
}

/** Haut d'axe « rond » au-dessus de la plus grande valeur (1, 2, 5 × 10ⁿ). */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

/** Tracé SVG d'une suite (x de pad à w − pad, y de h − pad à pad). */
export function linePath(values: number[], max: number, w: number, h: number, pad = 0): string {
  if (!values.length) return '';
  const step = values.length > 1 ? (w - 2 * pad) / (values.length - 1) : 0;
  return values
    .map((v, i) => {
      const x = pad + i * step;
      const y = h - pad - (Math.max(0, v) / max) * (h - 2 * pad);
      return `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

/**
 * Nom lisible d'une page : le titre du contenu s'il est connu (« SKILLCARD ·
 * Pythagore »), « Accueil » pour la racine, sinon le chemin.
 */
export function pageLabel(path: string, label?: string | null): string {
  if (label && label.trim()) return label.trim();
  return path === '/' ? 'Accueil' : path;
}

/**
 * Adresse à ouvrir pour une page vue : rien si le chemin est masqué (`:id`) ;
 * le site sur son domaine, l'app sur la sienne (chemin interne).
 */
export function pageHref(app: 'site' | 'app', path: string, siteOrigin: string): string | null {
  if (path.includes(':') || !path.startsWith('/')) return null;
  return app === 'site' ? `${siteOrigin.replace(/\/$/, '')}${path}` : path;
}

/** Robots de la période, du plus actif au moins actif. */
export function botRows(bots: Record<string, number>): { label: string; views: number }[] {
  return Object.entries(bots)
    .map(([k, v]) => ({ label: BOT_LABELS[k] ?? k, views: v }))
    .sort((a, b) => b.views - a.views || a.label.localeCompare(b.label));
}

/** Admin : chiffres en direct, rechargés toutes les 30 s. */
export function useLiveStats(range: StatsRange, enabled: boolean) {
  return useQuery({
    queryKey: ['admin-live-stats', range],
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<LiveStats> => {
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db.rpc('admin_live_stats', { p_range: range });
      if (error) throw error;
      return data as LiveStats;
    },
  });
}
